import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';

import { Button, Card, CardBody, Col } from 'reactstrap';
import { useConfirmApi, useProxyParameters } from 'utils/api/ApiHooks';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import { useMe } from 'utils/util/hooks';
import { getPrettyDate } from 'utils/util/util';
import EmptyState from 'views/Widgets/EmptyState';
import RichTextViewer from 'views/Widgets/Inputs/RichTextViewer';
import Loading from 'views/Widgets/Loading';
import ConfirmationDialogModal from 'views/Widgets/Modals/ConfirmationDialogModal';
import AvatarGroup from 'views/Widgets/People/AvatarGroup';
import FilterablePeopleTable from 'views/Widgets/People/FilterablePeopleTable';
import CustomSurveyQuestionActionsDropdown from './CustomSurveyQuestionActionsDropdown';
import CustomSurveyQuestionEditorModal from './CustomSurveyQuestionEditorModal';
import {
  type CustomSurveyQuestionData,
  QUESTION_STATUSES,
  QUESTION_TYPES,
} from './consts';
import ManageAdministratorsModal from './ManageAdministratorsModal';

const TABLE_COLUMNS = [
  'question_label',
  'type',
  'status',
  'owner',
  'administrators',
  'created_at',
  'actions',
] as const;

const CustomSurveyQuestionsTab: FC = () => {
  const { formatMessage, locale } = useIntl();
  const proxyParameters = useProxyParameters();
  const me = useMe();

  const [questions, setQuestions] = useState<
    CustomSurveyQuestionData[] | undefined
  >(undefined);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] =
    useState<CustomSurveyQuestionData | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingQuestion, setDeletingQuestion] =
    useState<CustomSurveyQuestionData | null>(null);
  // Not displayed - just an empty truthy marker so ConfirmationDialogModal's
  // validationErrors prop flips its isReadyForInput check back to true,
  // resetting the stuck spinner/disabled Delete button after a failed
  // request. The actual message is shown via toast below.
  const [deleteHasError, setDeleteHasError] = useState<object | undefined>(
    undefined
  );
  const [isManageAdminsModalOpen, setIsManageAdminsModalOpen] = useState(false);
  const [managingAdminsQuestion, setManagingAdminsQuestion] =
    useState<CustomSurveyQuestionData | null>(null);

  const callback = useCallback(
    (
      data: CustomSurveyQuestionData[] | null,
      error: unknown,
      hardError: unknown
    ): void => {
      if (!data || error || hardError) {
        return;
      }
      setQuestions(data);
    },
    []
  );

  const { status } = useConfirmApi<CustomSurveyQuestionData[]>({
    method: 'GET',
    url: 'custom-survey/questions/admin',
    callback,
  });

  const handleCreateClick = useCallback(() => {
    setEditingQuestion(null);
    setIsEditorOpen(true);
  }, []);

  const handleEditClick = useCallback(
    (questionId: number) => {
      const question = questions?.find((q) => q.id === questionId);
      if (question) {
        setEditingQuestion(question);
        setIsEditorOpen(true);
      }
    },
    [questions]
  );

  const toggleEditor = useCallback(() => {
    setIsEditorOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!isEditorOpen) {
      setEditingQuestion(null);
    }
  }, []);

  const handleSaved = useCallback(
    (data: CustomSurveyQuestionData, isEdit: boolean) => {
      if (isEdit) {
        setQuestions((prev) =>
          prev?.map((q) => (q.uuid === data.uuid ? data : q))
        );
        setEditingQuestion(data);
      } else {
        setQuestions((prev) => (prev ? [data, ...prev] : [data]));
      }
    },
    []
  );

  const handleDeleteClick = useCallback(
    (questionId: number) => {
      const question = questions?.find((q) => q.id === questionId);
      if (question) {
        setDeletingQuestion(question);
        setDeleteHasError(undefined);
        setIsDeleteModalOpen(true);
      }
    },
    [questions]
  );

  const handleDeleteConfirm = useCallback(() => {
    if (!deletingQuestion?.id) return;
    setDeleteHasError(undefined);

    ConfirmAPI.sendRequestToConfirm(
      'DELETE',
      `custom-survey/questions/admin/${deletingQuestion.id}`,
      proxyParameters,
      (_data, error, hardErrorMessage) => {
        if (error || hardErrorMessage) {
          let errorMessage = formatMessage({
            id: 'app.views.administration.custom_surveys.questions.delete_error',
            defaultMessage: 'Failed to delete question.',
          });

          if (!hardErrorMessage) {
            const detail = error?.data;
            const backendMsg = Array.isArray(detail) ? detail[0] : detail;
            if (typeof backendMsg === 'string') {
              errorMessage = backendMsg;
            }
          }
          toast.error(errorMessage);
          setDeleteHasError({});
          return;
        }
        setQuestions((prev) =>
          prev?.filter((q) => q.uuid !== deletingQuestion.uuid)
        );
        setIsDeleteModalOpen(false);
        setDeletingQuestion(null);
      }
    );
  }, [deletingQuestion, proxyParameters, formatMessage]);

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setDeletingQuestion(null);
    setDeleteHasError(undefined);
  }, []);

  const handleStatusChange = useCallback(
    (questionId: number, newStatus: string, errorMsg: string) => {
      ConfirmAPI.sendRequestToConfirm(
        'PATCH',
        `custom-survey/questions/admin/${questionId}`,
        { ...proxyParameters, status: newStatus },
        (data, error, hardErrorMessage) => {
          if (error || hardErrorMessage) {
            toast.error(errorMsg);
            return;
          }
          setQuestions((prev) =>
            prev?.map((q) => (q.uuid === data.uuid ? data : q))
          );
        }
      );
    },
    [proxyParameters]
  );

  const handleArchiveClick = useCallback(
    (questionId: number) =>
      handleStatusChange(
        questionId,
        'ARCHIVED',
        formatMessage({
          id: 'app.views.administration.custom_surveys.questions.archive_error',
          defaultMessage: 'Failed to archive question.',
        })
      ),
    [handleStatusChange, formatMessage]
  );

  const handleSetLiveClick = useCallback(
    (questionId: number) =>
      handleStatusChange(
        questionId,
        'ACTIVE',
        formatMessage({
          id: 'app.views.administration.custom_surveys.questions.restore_error',
          defaultMessage: 'Failed to restore question.',
        })
      ),
    [handleStatusChange, formatMessage]
  );

  const handleManageAdministratorsClick = useCallback(
    (questionId: number) => {
      const question = questions?.find((q) => q.id === questionId);
      if (question) {
        setManagingAdminsQuestion(question);
        setIsManageAdminsModalOpen(true);
      }
    },
    [questions]
  );

  const toggleManageAdminsModal = useCallback(() => {
    setIsManageAdminsModalOpen((prev) => !prev);
  }, []);

  const handleManageAdministratorsSave = useCallback(
    (data: Partial<CustomSurveyQuestionData>) => {
      if (!managingAdminsQuestion?.id) {
        return;
      }

      ConfirmAPI.sendRequestToConfirm(
        'PATCH',
        `custom-survey/questions/admin/${managingAdminsQuestion.id}`,
        { ...proxyParameters, administrator_ids: data.administrator_ids },
        (responseData, error, hardErrorMessage) => {
          if (error || hardErrorMessage) {
            toast.error(
              formatMessage({
                id: 'app.views.administration.custom_surveys.questions.manage_administrators.save_error',
                defaultMessage: 'Failed to update administrators.',
              })
            );
            return;
          }
          setQuestions((prev) =>
            prev?.map((q) => (q.uuid === responseData.uuid ? responseData : q))
          );
          setIsManageAdminsModalOpen(false);
        }
      );
    },
    [managingAdminsQuestion, proxyParameters, formatMessage]
  );

  // Clear managingAdminsQuestion when modal closes so stale data doesn't leak
  useEffect(() => {
    if (!isManageAdminsModalOpen) {
      setManagingAdminsQuestion(null);
    }
  }, [isManageAdminsModalOpen]);

  const questionTypes = useMemo(
    () => QUESTION_TYPES(formatMessage),
    [formatMessage]
  );
  const questionStatuses = useMemo(
    () => QUESTION_STATUSES(formatMessage),
    [formatMessage]
  );

  const COLUMN_DEFS = useMemo(
    () =>
      ({
        question_label: {
          name: formatMessage({
            id: 'app.views.administration.custom_surveys.questions.table.question_text',
            defaultMessage: 'Question text',
          }),
          field: 'question_label',
        },
        type: {
          name: formatMessage({
            id: 'app.views.administration.custom_surveys.questions.table.type',
            defaultMessage: 'Type',
          }),
          field: 'type',
          sortable: false,
        },
        status: {
          name: formatMessage({
            id: 'app.views.administration.custom_surveys.questions.table.status',
            defaultMessage: 'Status',
          }),
          field: 'status',
          sortable: false,
        },
        owner: {
          name: formatMessage({
            id: 'app.views.administration.custom_surveys.questions.table.owner',
            defaultMessage: 'Owner',
          }),
          field: 'owner',
          sortable: false,
        },
        administrators: {
          name: formatMessage({
            id: 'app.views.administration.custom_surveys.questions.table.administrators',
            defaultMessage: 'Administrators',
          }),
          field: 'administrators',
          sortable: false,
        },
        created_at: {
          name: formatMessage({
            id: 'app.views.administration.custom_surveys.questions.table.created',
            defaultMessage: 'Created',
          }),
          field: 'created_at',
          sortable: false,
          format: (date: string) =>
            getPrettyDate({ dateString: date, includeTime: false, locale }),
        },
        actions: {
          name: formatMessage({
            id: 'app.views.administration.custom_surveys.questions.table.actions',
            defaultMessage: 'Actions',
          }),
          field: 'actions',
          className: 'text-end text-nowrap',
          sortable: false,
        },
      }) as const,
    [formatMessage, locale]
  );

  const columns = useMemo(
    () => TABLE_COLUMNS.map((key) => COLUMN_DEFS[key]),
    [COLUMN_DEFS]
  );

  const isQuestionOwnerOrAdmin = useCallback(
    (q: CustomSurveyQuestionData) =>
      me?.id === q.owner?.id ||
      (q.administrators ?? []).some((a) => a.id === me?.id),
    [me]
  );

  const toRow = useCallback(
    (q: CustomSurveyQuestionData, isOwnerOrAdmin: boolean) => {
      const questionObj = q.question as Record<string, unknown>;
      return {
        id: q.id,
        question_label: (
          <RichTextViewer
            model={questionObj?.label as string | undefined}
            expanded
          />
        ),
        type:
          questionTypes.find((t) => t.id === questionObj?.type)?.name ??
          (questionObj?.type as string) ??
          '-',
        status:
          questionStatuses.find((s) => s.id === q.status)?.name ?? q.status,
        owner: q.owner ? <AvatarGroup people={[q.owner]} maxFaces={1} /> : '-',
        administrators: !!q.administrators?.length && (
          <AvatarGroup people={q.administrators} maxFaces={2} />
        ),
        created_at: q.created_at,
        actions: (
          <div className="d-flex align-items-center justify-content-end gap-2">
            <CustomSurveyQuestionActionsDropdown
              questionId={q.id!}
              isOwnerOrAdmin={isOwnerOrAdmin}
              status={q.status}
              hasAnswers={!!q.has_answers}
              onEditClick={handleEditClick}
              onManageAdministratorsClick={handleManageAdministratorsClick}
              onArchiveClick={handleArchiveClick}
              onSetLiveClick={handleSetLiveClick}
              onDeleteClick={handleDeleteClick}
            />
          </div>
        ),
      };
    },
    [
      handleEditClick,
      handleManageAdministratorsClick,
      handleArchiveClick,
      handleSetLiveClick,
      handleDeleteClick,
      questionTypes,
      questionStatuses,
    ]
  );

  // Sort order is computed once from the initial fetch and preserved across
  // status changes so that rows don't jump around when archiving / restoring.
  const sortOrderRef = useRef<string[]>([]);

  const rows = useMemo(() => {
    if (!questions) return [];

    const hasOrder = sortOrderRef.current.length > 0;
    if (!hasOrder) {
      // First render: compute sort order and persist it
      const sorted = [...questions].sort((a, b) => {
        const aIsOwn = isQuestionOwnerOrAdmin(a);
        const bIsOwn = isQuestionOwnerOrAdmin(b);
        if (aIsOwn !== bIsOwn) return aIsOwn ? -1 : 1;
        if (a.status !== b.status) return a.status === 'ACTIVE' ? -1 : 1;
        const aLabel =
          ((a.question as Record<string, unknown>)?.label as string) ?? '';
        const bLabel =
          ((b.question as Record<string, unknown>)?.label as string) ?? '';
        return aLabel.localeCompare(bLabel);
      });
      sortOrderRef.current = sorted.map((q) => q.uuid!);
      return sorted.map((q) => toRow(q, isQuestionOwnerOrAdmin(q)));
    }

    // Subsequent renders: preserve original order, append new questions at top
    const byUuid = new Map(questions.map((q) => [q.uuid!, q]));
    const ordered: CustomSurveyQuestionData[] = [];
    const seen = new Set<string>();

    // New questions (not in the original sort order) go first
    for (const q of questions) {
      if (!sortOrderRef.current.includes(q.uuid!)) {
        ordered.push(q);
        seen.add(q.uuid!);
      }
    }
    // Then the original order
    for (const uuid of sortOrderRef.current) {
      const q = byUuid.get(uuid);
      if (q && !seen.has(uuid)) {
        ordered.push(q);
      }
    }
    // Update ref so new questions are included in future iterations
    sortOrderRef.current = ordered.map((q) => q.uuid!);
    return ordered.map((q) => toRow(q, isQuestionOwnerOrAdmin(q)));
  }, [questions, isQuestionOwnerOrAdmin, toRow]);

  if (status === 'LOADING') {
    return (
      <Loading
        message={formatMessage({
          id: 'app.views.administration.custom_surveys.questions.loading',
          defaultMessage: 'Loading questions...',
        })}
      />
    );
  }

  const hasNoQuestions = !questions?.length;

  return (
    <div className="container-fluid">
      {hasNoQuestions ? (
        <Card>
          <CardBody>
            <EmptyState
              title={formatMessage({
                id: 'app.views.administration.custom_surveys.questions.empty.title',
                defaultMessage: 'No questions yet',
              })}
              subtitle={formatMessage({
                id: 'app.views.administration.custom_surveys.questions.empty.subtitle',
                defaultMessage:
                  'Create reusable questions that can be added to lifecycle surveys.',
              })}
            >
              <Button color="primary" onClick={handleCreateClick}>
                <FormattedMessage
                  id="app.views.administration.custom_surveys.questions.empty.create_button"
                  defaultMessage="Create your first question"
                />
              </Button>
            </EmptyState>
          </CardBody>
        </Card>
      ) : (
        <FilterablePeopleTable
          columns={columns}
          rows={rows}
          hideFilters={true}
          hideExportButton={true}
          actions={
            <Col className="col-auto ps-0 ms-auto d-flex flex-wrap gap-2 align-items-center">
              <Button color="primary" onClick={handleCreateClick}>
                <FormattedMessage
                  id="app.views.administration.custom_surveys.questions.create_button"
                  defaultMessage="Create question"
                />
              </Button>
            </Col>
          }
        />
      )}

      <CustomSurveyQuestionEditorModal
        isOpen={isEditorOpen}
        toggle={toggleEditor}
        editingQuestion={editingQuestion}
        onSaved={handleSaved}
      />

      <ManageAdministratorsModal
        isOpen={isManageAdminsModalOpen}
        toggle={toggleManageAdminsModal}
        onSave={handleManageAdministratorsSave}
        entity={managingAdminsQuestion}
      />

      {isDeleteModalOpen && deletingQuestion && (
        <ConfirmationDialogModal
          isOpen={isDeleteModalOpen}
          onClosed={handleCloseDeleteModal}
          toggle={handleCloseDeleteModal}
          confirmCallback={handleDeleteConfirm}
          title={formatMessage({
            id: 'app.views.administration.custom_surveys.questions.delete_question.title',
            defaultMessage: 'Delete question',
          })}
          description={formatMessage({
            id: 'app.views.administration.custom_surveys.questions.delete.description',
            defaultMessage:
              'Are you sure you want to delete this question? This cannot be undone.',
          })}
          confirmText={formatMessage({
            id: 'app.views.administration.custom_surveys.questions.delete.confirm',
            defaultMessage: 'Delete',
          })}
          validationErrors={deleteHasError}
        />
      )}
    </div>
  );
};

export default React.memo(CustomSurveyQuestionsTab);
